/**
 * OpenPanel funnel — same stages as Meta / Plausible.
 *
 * - Page land: named events per route (HomepageLanded, BlogPageLanded, …) via useOpenPanelPageLand
 * - ViewContent: pooja detail OR /book land
 * - AddToCart: Book Now click OR /book land
 * - InitiateCheckout: booking validated / payment starting
 * - Purchase: Razorpay success (deduped by bookingId; not thank-you revisit)
 *
 * UTM / campaign / click_id are merged onto every custom event.
 */

import { isGoogleAnalyticsAllowedOnCurrentHost } from '../config/googleAnalytics';
import { isOpenPanelAllowedOnCurrentHost } from '../config/openpanel';
import { trackGoogleAnalyticsMirrorEvent } from './googleAnalytics';
import { getOpenPanelAttributionPayload, getOpenPanelClients } from './openpanelInit';

const FLAG_E0_PREFIX = 'daiv_openpanel_sent_e0_';
const FLAG_ATC_PREFIX = 'daiv_openpanel_sent_atc_';
const FLAG_E1_PREFIX = 'daiv_openpanel_sent_e1_';
const FLAG_E2_PREFIX = 'daiv_openpanel_sent_e2_';
const ALLOW_REPEAT_IN_DEV = process.env.REACT_APP_OPENPANEL_ALLOW_REPEAT_IN_DEV === 'true';

function shouldDeduplicateInSession() {
  if (process.env.NODE_ENV === 'production') return true;
  return !ALLOW_REPEAT_IN_DEV;
}

function canTrackOpenPanel() {
  return typeof window !== 'undefined' && isOpenPanelAllowedOnCurrentHost();
}

function canTrackAnyAnalytics() {
  return canTrackOpenPanel() || isGoogleAnalyticsAllowedOnCurrentHost();
}

function contentProps(slug, contentName) {
  const id = slug ? String(slug) : '';
  return {
    content_type: 'product',
    content_id: id,
    content_name: String(contentName || id || ''),
  };
}

function sessionFlagOnce(key) {
  if (!key || !shouldDeduplicateInSession()) return true;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

function fire(name, props = {}) {
  const attribution = getOpenPanelAttributionPayload();
  const payload = {
    ...attribution,
    ...(props && typeof props === 'object' ? props : {}),
  };

  trackGoogleAnalyticsMirrorEvent(name, payload);

  if (!canTrackOpenPanel()) return;
  const clients = getOpenPanelClients();
  if (!clients.length) return;

  const body = Object.keys(payload).length ? payload : undefined;
  clients.forEach((client) => {
    if (typeof client.track === 'function') client.track(name, body);
  });
}

/** SPA route land — fires on every navigation (HomepageLanded, BlogPageLanded, …). */
export function tryFireOpenPanelPageLandEvent(eventName, props = {}) {
  const name = String(eventName || '').trim();
  if (!name) return;
  fire(name, props);
}

/** Book Now click or /book deep-link — once per slug per tab session */
export function tryFireOpenPanelBookNowClick(poojaSlug, contentName, valueInr) {
  if (!canTrackAnyAnalytics() || !poojaSlug) return;
  if (!sessionFlagOnce(`${FLAG_ATC_PREFIX}${poojaSlug}`)) return;
  const value = Number(valueInr);
  fire('AddToCart', {
    ...contentProps(poojaSlug, contentName),
    ...(Number.isFinite(value) && value > 0 ? { value, currency: 'INR' } : {}),
  });
}

/** Pooja detail or /book land — once per slug per tab session */
export function tryFireOpenPanelPoojaPageView(poojaSlug, contentName) {
  if (!canTrackAnyAnalytics() || !poojaSlug) return;
  if (!sessionFlagOnce(`${FLAG_E0_PREFIX}${poojaSlug}`)) return;
  fire('ViewContent', contentProps(poojaSlug, contentName));
}

/** Booking step-1 complete / payment starting — once per slug per tab */
export function tryFireOpenPanelBookingCheckoutStart(poojaSlug, contentName, valueInr) {
  if (!canTrackAnyAnalytics() || !poojaSlug) return;
  if (!sessionFlagOnce(`${FLAG_E1_PREFIX}${poojaSlug}`)) return;
  const value = Number(valueInr);
  fire('InitiateCheckout', {
    ...contentProps(poojaSlug, contentName),
    ...(Number.isFinite(value) && value > 0 ? { value, currency: 'INR' } : {}),
  });
}

/**
 * Payment success — once per bookingId (preferred) or slug per tab.
 * Fires both:
 * - custom `Purchase` with `value` (INR) — existing reports keep working
 * - `op.revenue()` in paise — fills Overview Revenue card
 * @param {string} [bookingId]
 */
export function tryFireOpenPanelPurchase(amountInr, poojaSlug, contentName, bookingId) {
  if (!canTrackAnyAnalytics()) return;
  const value = Number(amountInr);
  const amount = Number.isFinite(value) && value >= 0 ? value : 0;
  const dedupeId = String(bookingId || poojaSlug || '').trim();
  if (dedupeId && !sessionFlagOnce(`${FLAG_E2_PREFIX}${dedupeId}`)) return;

  const baseProps = {
    ...contentProps(poojaSlug, contentName),
    ...(bookingId ? { booking_id: String(bookingId) } : {}),
  };

  fire('Purchase', {
    ...baseProps,
    value: amount,
    currency: 'INR',
  });

  if (!canTrackOpenPanel()) return;

  // Overview Revenue uses dedicated revenue events (smallest currency unit = paise).
  const paise = Math.max(0, Math.round(amount * 100));
  const attribution = getOpenPanelAttributionPayload();
  getOpenPanelClients().forEach((client) => {
    if (typeof client.revenue !== 'function') return;
    try {
      client.revenue(paise, {
        ...attribution,
        ...baseProps,
        currency: 'INR',
      });
    } catch {
      // ignore SDK / project-setting failures; Purchase still recorded
    }
  });
}
